import { Polyline } from 'react-leaflet'
import { useMemo } from 'react'
import { VAN_ROUTE_POINTS } from '../../utils/vanRouteData'
import type { LegAssignment } from '../../types/index'

export type VanRouteMode = 'off' | 'current' | 'all'

interface VanRouteProps {
  mode: VanRouteMode
  currentLeg: LegAssignment | undefined
}

function distSq(lat1: number, lng1: number, lat2: number, lng2: number) {
  const dlat = lat1 - lat2
  const dlng = lng1 - lng2
  return dlat * dlat + dlng * dlng
}

function nearestIndex(lat: number, lng: number) {
  let best = 0
  let bestD = Infinity
  for (let i = 0; i < VAN_ROUTE_POINTS.length; i++) {
    const d = distSq(lat, lng, VAN_ROUTE_POINTS[i][0], VAN_ROUTE_POINTS[i][1])
    if (d < bestD) { bestD = d; best = i }
  }
  return best
}

const activeStyle   = { color: '#6b21a8', weight: 4, opacity: 0.9, dashArray: '8, 6' } as const

export function VanRoute({ mode, currentLeg }: VanRouteProps) {
  const segments = useMemo(() => {
    if (mode === 'off') return null

    if (mode === 'all') {
      if (!currentLeg) {
        return { before: VAN_ROUTE_POINTS, active: [], after: [] }
      }
      const fromIdx = nearestIndex(currentLeg.startLat, currentLeg.startLng)
      const toIdx   = nearestIndex(currentLeg.endLat,   currentLeg.endLng)
      const lo = Math.min(fromIdx, toIdx)
      const hi = Math.max(fromIdx, toIdx)
      return {
        before: VAN_ROUTE_POINTS.slice(0, lo + 1),
        active: VAN_ROUTE_POINTS.slice(lo, hi + 1),
        after:  VAN_ROUTE_POINTS.slice(hi),
      }
    }

    // mode === 'current': only the active driving segment
    if (!currentLeg) return null
    const fromIdx = nearestIndex(currentLeg.startLat, currentLeg.startLng)
    const toIdx   = nearestIndex(currentLeg.endLat,   currentLeg.endLng)
    const lo = Math.min(fromIdx, toIdx)
    const hi = Math.max(fromIdx, toIdx)
    return {
      before: [],
      active: VAN_ROUTE_POINTS.slice(lo, hi + 1),
      after:  [],
    }
  }, [mode, currentLeg])

  if (!segments) return null

  return (
    <>
      {segments.before.length > 1 && <Polyline positions={segments.before} {...activeStyle} />}
      {segments.active.length > 1  && <Polyline positions={segments.active} {...activeStyle} />}
      {segments.after.length  > 1  && <Polyline positions={segments.after}  {...activeStyle} />}
    </>
  )
}
